import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  FormControl,
  FormLabel,
  Select,
  FormErrorMessage,
  VStack,
  Box,
  Text,
  Flex,
  HStack,
  Icon,
  useToast,
} from "@chakra-ui/react";
import { FiClock } from "react-icons/fi";
import axiosInstance from "../../helpers/axiosInstance";
import { useCurrency } from "../CurrencyExchange/CurrencyContext";

export default function AddFlightItemModal({
  isOpen,
  onClose,
  tripId,
  setRouteData,
  setFlights,
  routes = [],
}) {
  const toast = useToast();
  const { convertCurrency, currency: displayCurrency } = useCurrency();

  const [services, setServices] = useState([]);
  const [serviceId, setServiceId] = useState("");
  const [itemData, setItemData] = useState(null);
  const [errors, setErrors] = useState({});
  // Remove selectedRoute state
  // const [selectedRoute, setSelectedRoute] = useState("");
  const [derivedRoute, setDerivedRoute] = useState("");

  /* ------------------------------------------------------------- */
  // Fetch available flight services when the modal opens
  useEffect(() => {
    if (!isOpen) return;

    setErrors({});
    setServiceId("");
    setItemData(null);
    // setSelectedRoute("");
    setDerivedRoute("");

    (async () => {
      try {
        const res = await axiosInstance.get(
          "/bucket-list/filter-third-party-services?category=transportation&preference=flight"
        );
        setServices(res.data.data?.services || []);
      } catch (err) {
        console.error("Error fetching flights:", err);
      }
    })();
  }, [isOpen]);

  /* ------------------------------------------------------------- */
  const validate = () => {
    const e = {};
    if (!serviceId) e.serviceId = "Flight selection is required";
    // Remove route validation
    // if (!selectedRoute) e.selectedRoute = "Route selection is required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    /* ---------- back-end payload ---------- */
    const payload = {
      service_id: serviceId,
      item_data: itemData,
      type: "flight",
      // route: selectedRoute, // ← send route to server
    };

    try {
      const res = await axiosInstance.post(
        `/bucket-list/${tripId}/add-service-item`,
        payload
      );

      /* ---------- optimistic UI update ---------- */
      if (res.data.success) {
        // Create a *deep* copy of the current routes array
        const newRoutes = routes.map((r) => ({
          ...r,
          flights: [...(r.flights || [])],
        }));

        // Use derivedRoute instead of selectedRoute
        const routeIdx = newRoutes.findIndex((r) => r.route === derivedRoute);

        if (routeIdx === -1) {
          // Route doesn't exist yet → create it
          newRoutes.push({
            route: derivedRoute,
            flights: [itemData],
          });
        } else {
          // Push into existing route
          newRoutes[routeIdx].flights.push(itemData);
        }

        /* Push state back up so FlightsList re-renders */
        setRouteData?.(newRoutes);
        setFlights?.(newRoutes);

        toast({
          title: "Flight added!",
          description:
            res.data.message || "The new flight is now in your list.",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (err) {
      console.error("Error adding flight item:", err);
      toast({
        title: "Could not add flight.",
        description: "Please try again.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }

    onClose();
  };

  /* ------------------------------------------------------------- */
  // Price display helper
  let displayPrice = "";
  if (itemData) {
    if (typeof itemData.price === "number") {
      // raw numeric price → assume USD
      const conv = convertCurrency(itemData.price, "USD");
      displayPrice = (typeof conv === "number" ? conv : itemData.price).toFixed(
        2
      );
    } else if (itemData.price && typeof itemData.price.total === "number") {
      const conv = convertCurrency(
        itemData.price.total,
        itemData.price.currency
      );
      displayPrice = (
        typeof conv === "number" ? conv : itemData.price.total
      ).toFixed(2);
    }
  }

  /* ----------------------- JSX ----------------------- */
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent maxH="80vh" overflowY="auto">
        <ModalHeader>Add Flight from Bucket List</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            {/* Route selector - removed */}
            {/*
            <FormControl isInvalid={!!errors.selectedRoute}>
              <FormLabel>Route</FormLabel>
              <Select
                placeholder="Select route"
                value={selectedRoute}
                onChange={(e) => setSelectedRoute(e.target.value)}
              >
                {routes.map((r, idx) => (
                  <option
                    key={typeof r === "string" ? r : r.route}
                    value={typeof r === "string" ? r : r.route}
                  >
                    {typeof r === "string" ? r : r.route}
                  </option>
                ))}
              </Select>
              <FormErrorMessage>{errors.selectedRoute}</FormErrorMessage>
            </FormControl>
            */}

            {/* Flight selector */}
            <FormControl isInvalid={!!errors.serviceId}>
              <FormLabel>Flight</FormLabel>
              <Select
                placeholder="Select flight"
                value={serviceId}
                onChange={(e) => {
                  const id = e.target.value;
                  setServiceId(id);

                  // Find the service and map its data.price → { total, currency }
                  const sel = services.find((s) => s._id === id);
                  if (!sel) return;

                  const raw = sel.data;
                  const priceVal = raw.price;
                  const priceObj =
                    typeof priceVal === "number"
                      ? { total: priceVal, currency: "USD" }
                      : raw.price;

                  setItemData({
                    ...raw,
                    price: raw.price,
                  });

                  // Use formatRoute from FlightsList to ensure route string matches
                  const routeStr = `${raw.origin} ➔ ${raw.destination}`;
                  // Use the same formatting logic as FlightsList
                  // If formatRoute is not exported, copy its logic here
                  setDerivedRoute(routeStr);
                }}
              >
                {services.map((s) => (
                  <option key={s._id} value={s._id}>
                    {`${s.data.origin_airport} → ${
                      s.data.destination_airport
                    } (${new Date(s.data.departure_at).toLocaleDateString()})`}
                  </option>
                ))}
              </Select>
              <FormErrorMessage>{errors.serviceId}</FormErrorMessage>
            </FormControl>

            {/* Preview card */}
            {itemData && (
              <Box borderWidth="1px" borderRadius="md" p={4} mt={2}>
                {/* Top row: carrier code + route (left), price (right) */}
                <Flex justify="space-between" align="center">
                  <Box>
                    <Text fontSize="lg" fontWeight="bold" mb={0}>
                      {itemData.airline || itemData.carrier_code}
                    </Text>
                    <Text fontSize="md" color="gray.600" mb={0}>
                      {`${itemData.origin_airport} → ${itemData.destination_airport}`}
                    </Text>
                  </Box>
                  <Text fontSize="lg" fontWeight="bold" mb={0}>
                    {displayCurrency} {displayPrice}
                  </Text>
                </Flex>
                {/* Departure / Return lines */}
                <Box mt={2}>
                  <Text fontSize="sm" mb={0}>
                    Departure: From {itemData.origin || itemData.origin_airport}{" "}
                    at {new Date(itemData.departure_at).toLocaleDateString()}
                  </Text>
                  {itemData.return_at && (
                    <Text fontSize="sm" mb={0}>
                      Return: From{" "}
                      {itemData.destination || itemData.destination_airport} at{" "}
                      {new Date(itemData.return_at).toLocaleDateString()}
                    </Text>
                  )}
                </Box>

                {/* Duration & stops */}
                <HStack mt={3} spacing={2} color="gray.500">
                  <Icon as={FiClock} />
                  <Text fontSize="sm" mb={0}>
                    {itemData.duration}m •{" "}
                    {itemData.transfers === 0
                      ? "Non-stop"
                      : `${itemData.transfers} stop${
                          itemData.transfers > 1 ? "s" : ""
                        }`}
                  </Text>
                </HStack>
              </Box>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button onClick={onClose} mr={3} variant="ghost">
            Cancel
          </Button>
          <Button colorScheme="customGreen" onClick={handleSave}>
            Add Flight
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
